import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ViewTableComponent } from '../../base/view-table/view-table.component';
import { FrmManageStaffComponent } from '../../forms/frm-manage-staff/frm-manage-staff.component';
import { ComponentType } from '@angular/cdk/overlay';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { LoggerService } from '../../../services/logger.service';

type StaffRecord = {
  uid: number;
  field_first_name: string;
  field_last_name: string;
  mail: string;
  field_cell_number: string;
  status: string | number | boolean;
  access?: string;
  created?: string;
  roles_target_id?: string;
  studio_roles?: string[];
};

@Component({
  selector: 'app-staff-management',
  templateUrl: './staff-management.component.html',
  styleUrls: ['./staff-management.component.css']
})
export class StaffManagementComponent extends ViewTableComponent implements OnInit {
  private readonly hiddenRoleNames = new Set(['administrator', 'studio administrator']);

  frmManageStaffComponent = FrmManageStaffComponent

  override dataSource: MatTableDataSource<StaffRecord>;

  links = [
    { label: 'Active', path: '/configuration/staff-management', queryParams: { status: '1' } },
    { label: 'Inactive', path: '/configuration/staff-management', queryParams: { status: '0' } }
  ];

  activeLink = this.links[0];

  override displayedColumns: string[] = [
    'name',
    'contact',
    'status',
    'activity',
    'created',
    'roles',
    'actions',
  ];

  tableSearchValue = '';
  /** The toolbar search is collapsed to an icon until invoked. */
  searchOpen = false;
  @ViewChild('quickSearchInput') quickSearchInput?: ElementRef<HTMLInputElement>;
  lastRefreshedAt: Date | null = null;
  metrics = {
    total: 0,
    active: 0,
    inactive: 0,
  };

  /** Map of role id → display label, loaded from config. */
  private roleLabelMap: Record<string, string> = {};

  /** Map of uid → studio role labels for the current studio. */
  private studioRolesByUid: Record<number, string[]> = {};

  override ngOnInit() {
    // Hold the loading state until getData() runs, so "No staff in this view"
    // cannot paint before the first response.
    this.ShowProgressBar = true;
    this.activeLink = this.links[0]; // Set the default active link

    // Load role config once to get display labels.
    this._drupalRESTService.httpGET('/api/studio-permissions/config').subscribe((res: any) => {
      if (res?.success && res.data?.roles) {
        for (const [id, def] of Object.entries<any>(res.data.roles)) {
          if (this.isVisibleRole(id) && this.isVisibleRole(def.label || id)) {
            this.roleLabelMap[id] = def.label || id;
          }
        }
      }
    });

    this._activatedRoute.queryParams.subscribe(params => {
      const status = params['status'];
      if (status === '1') {
        this.activeLink = this.links[0];
      } else if (status === '0') {
        this.activeLink = this.links[1];
      }

      const queryParams = status ? [{ parameter: 'status', value: status }] : [];
      this.getData(queryParams);
    });
  }

  override getData(params: { parameter: string; value: string; }[]) {
    this.ShowProgressBar = true;
    this.queryParams = params;

    const queryParams: string[] = [];
    if (params) {
      for (const param of params) {
        if (param.parameter === 'status') {
          queryParams.push(`status=${param.value}`);
        }
      }
    }

    // Load the data with the filtering parameters
    const filters = queryParams.length > 0 ? '?' + queryParams.join('&') : '';
    const endpoint: string = "/api/v1/instructor_list" + filters;

    this._drupalRESTService.httpGET(endpoint, params)
      .subscribe(
        (data) => {
          this.ShowProgressBar = false;
          const rows: StaffRecord[] = data?.['results'] ?? [];
          this.dataSource = new MatTableDataSource<StaffRecord>(rows);
          this.configureFilterPredicate();
          this.updateTableInsights(rows);
          if (data?.['results']) {
            this.setupPagination(data, params);
          }
          this.loadStudioRoleAssignments(rows);
        },
        error => this.handleError(error)
      );
  }

  /**
   * Load per-studio role assignments and merge them into staff records.
   */
  private loadStudioRoleAssignments(rows: StaffRecord[]): void {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId || rows.length === 0) {
      return;
    }

    this._drupalRESTService.httpGET(`/api/studio-permissions/studio/${studioId}/assignments`).subscribe({
      next: (res: any) => {
        this.studioRolesByUid = {};
        if (res?.success && Array.isArray(res.data)) {
          for (const assignment of res.data) {
            const uid = +assignment.uid;
            this.studioRolesByUid[uid] = (assignment.roles || []).map(
              (roleId: string) => this.roleLabelMap[roleId] || roleId
            ).filter(role => this.isVisibleRole(role));
          }
        }
        // Merge into rows.
        for (const row of rows) {
          row.studio_roles = this.studioRolesByUid[+row.uid] || [];
        }
      },
      error: () => {
        // User may lack staff_permissions, so leave studio_roles empty.
      },
    });
  }

  refreshView() {
    this.getData(this.queryParams);
  }

  applyQuickFilter(value: string) {
    this.tableSearchValue = value;
    if (!this.dataSource) {
      return;
    }

    this.dataSource.filter = value.trim().toLowerCase();
  }

  openSearch() {
    this.searchOpen = true;
    setTimeout(() => this.quickSearchInput?.nativeElement.focus());
  }

  /** Closes the search and clears any active filter with it. */
  closeSearch() {
    this.searchOpen = false;
    this.applyQuickFilter('');
  }

  clearQuickFilter() {
    if (!this.tableSearchValue) {
      return;
    }

    this.tableSearchValue = '';
    this.applyQuickFilter('');
  }

  statusLabel(row: StaffRecord): string {
    return this.isActive(row) ? 'Active' : 'Inactive';
  }

  statusChipColor(row: StaffRecord): 'primary' | 'warn' {
    return this.isActive(row) ? 'primary' : 'warn';
  }

  rolesList(row: StaffRecord): string[] {
    // Prefer per-studio roles when available.
    if (row?.studio_roles && row.studio_roles.length > 0) {
      return row.studio_roles;
    }
    // Fall back to Drupal user roles.
    if (!row?.roles_target_id) {
      return [];
    }
    return row.roles_target_id
      .split(',')
      .map(role => role.trim())
      .filter(role => this.isVisibleRole(role));
  }

  private isVisibleRole(role: string): boolean {
    return !this.hiddenRoleNames.has((role || '').trim().toLowerCase());
  }

  trackByUid(_index: number, row: StaffRecord) {
    return row?.uid ?? _index;
  }

  get hasResults(): boolean {
    return !!this.dataSource && this.dataSource.data.length > 0;
  }

  initials(row: StaffRecord): string {
    const first = row?.field_first_name?.charAt(0) ?? '';
    const last = row?.field_last_name?.charAt(0) ?? '';
    return `${first}${last}`.trim().toUpperCase() || 'SM';
  }

  usernameFromEmail(row: StaffRecord): string | null {
    if (!row?.mail || !row.mail.includes('@')) {
      return null;
    }
    return row.mail.split('@')[0];
  }

  setStudentArchive(student_account_id: number, value: boolean) {
    let body = {
      field_archive: '' + value,
    };
    let setStudentArchive = this._entityRESTService.patchEntity('student_accounts', 'student_account', student_account_id, body);
    setStudentArchive.subscribe(data => {
      LoggerService.log('data', data);
      this.getData(this.queryParams);
    })
  }



  /**
   * Utility to open dialogs with a default configuration.
   *
   * @param component Component to be opened in the dialog.
   * @param uid Unique identifier for the staff member.
   * @param dialogConfig Configuration for the dialog.
   */
  openStaffDialog(component: ComponentType<unknown>, uid: number, action: string, dialogConfig: { height: string, width: string, closeOnNavigation?: boolean, data?: {} } = { height: "auto", width: "600px", closeOnNavigation: true }) {
    // Include the UID and action in the dialog's data
    dialogConfig.data = { ...dialogConfig.data, uid, action };

    let dialogRef = this._dialogService.openDialog(component, "defaultWithData", dialogConfig).afterClosed().subscribe(data => {
      // Handle actions after the dialog is closed, if necessary
      this.getData(this.queryParams);
    });
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.dataSource.data, event.previousIndex, event.currentIndex);
    this.dataSource = new MatTableDataSource(this.dataSource.data);

    // Prepare data to send
    const updatedOrder = this.dataSource.data.map(item => item?.['uid']);
    LoggerService.log("updatedOrder",updatedOrder)
    // Send POST request
    this._drupalRESTService.httpPOST('/api_rest/v1/updateInstructorSortOrder', { instructors: updatedOrder })
      .subscribe(response => {
        LoggerService.log(response);
        // Handle response here
      }, error => {
        LoggerService.error('Error updating instructor order', error);
      });
  }

  private configureFilterPredicate() {
    if (!this.dataSource) {
      return;
    }

    this.dataSource.filterPredicate = (data: StaffRecord, filter: string) => {
      const searchTarget = [
        data.field_first_name,
        data.field_last_name,
        data.mail,
        data.field_cell_number,
        data.roles_target_id,
        ...(data.studio_roles || []),
      ].join(' ').toLowerCase();

      return searchTarget.includes(filter);
    };

    if (this.tableSearchValue) {
      this.dataSource.filter = this.tableSearchValue.trim().toLowerCase();
    }
  }

  private updateTableInsights(rows: StaffRecord[] = []) {
    const total = rows.length;
    const active = rows.filter(row => this.isActive(row)).length;
    this.metrics = {
      total,
      active,
      inactive: total - active
    };
    this.lastRefreshedAt = new Date();
  }

  private isActive(row: StaffRecord): boolean {
    const statusValue = row?.status;
    if (typeof statusValue === 'boolean') {
      return statusValue;
    }

    if (typeof statusValue === 'number') {
      return statusValue === 1;
    }

    const normalized = (statusValue ?? '').toString().toLowerCase();
    return normalized === '1' || normalized === 'active' || normalized === 'true';
  }

}
